import { modalActions } from "@context/modals";

import classNames from "classnames";
import dynamic from "next/dynamic";
import { FC, MouseEvent, RefObject, useReducer } from "react";
import { MODAL_TYPES } from "types/modals";
import { PLAY_RATES } from "..";
import { PlayerSettings, Source, SpeedRate } from "@types";
import FastIcon from "@svg/player/Fast";
import VolumeProgress from "../volume-progress";
import { toFarsiNumber } from "@utils/digits";

// icons
import BackIcon from "@svg/player/Back";
import PauseIcon from "@svg/player/Pause";
import PlayIcon from "@svg/player/Play";
import QualityIcon from "@svg/player/Quality";
import PipIcon from "@svg/player/Pip";
import SubtitleIcon from "@svg/player/subtitle";
import FullscreenIcon from "@svg/player/Fullscreen";
import UnFullscreenIcon from "@svg/player/UnFullscreeen";
import VolumeLow from "@svg/player/VolumeLow";
import VolumeMedium from "@svg/player/VolumeMedium";
import VolumeHigh from "@svg/player/VolumeHigh";
import Mute from "@svg/player/Mute";
import Tooltip from "@shared/components/tooltip/Tooltip";
import { TOOLTIP_THEME } from "@shared/components/tooltip/constants";

const QualityList = dynamic(() => import("../quality-list"), { ssr: false });
const SpeedRateList = dynamic(() => import("../speed-rate-list"), {
  ssr: false,
});

interface Props {
  // properties
  isMobile: boolean;
  isPlaying: boolean;
  controlsIconProps: any;
  showGoNextTimer: boolean;
  settings: PlayerSettings;
  sources: Source[];
  isLandscape: boolean;
  elapsedRef: RefObject<HTMLTimeElement | null>;
  durationRef: RefObject<HTMLTimeElement | null>;

  // actions
  togglePlayByClickHandler: (
    e: MouseEvent<HTMLElement | SVGSVGElement>
  ) => void;
  backOrForWardHandler: (backward: boolean) => void;
  toggleMuteHandler: () => void;
  volumeChangeHandler: (volume: number) => void;
  speedRateChangeHandler: (rate: SpeedRate) => void;
  qualityChangeHandler: (quality: string) => void;
  toggleFullscreenHandler: () => void;
  toggleSubtitle: () => void;
  togglePipHandler: () => void;
}

const VideoControlsBottomActions: FC<Props> = ({
  isMobile,
  isPlaying,
  controlsIconProps,
  showGoNextTimer,
  settings,
  sources,
  isLandscape,
  elapsedRef,
  durationRef,

  togglePlayByClickHandler,
  backOrForWardHandler,
  toggleMuteHandler,
  volumeChangeHandler,
  speedRateChangeHandler,
  qualityChangeHandler,
  toggleFullscreenHandler,
  toggleSubtitle,
  togglePipHandler,
}) => {
  const {
    volume,
    fullScreen,
    pipEnable,
    fullScreenEnable,
    isMuted,
    pipSupported,
    withSubtitle,
    subtitleOn,
  } = settings || {};

  const [showVolumeProgress, toggleShowVolumeProgress] = useReducer(
    (prev) => !prev,
    false
  );

  const toggleRateSpeedModal = () =>
    modalActions.addModal(MODAL_TYPES.MOBILE_SPEED_RATE_LIST, {
      config: {
        hideCloseIcon: true,
      },
      data: {
        onSpeedRateChange: speedRateChangeHandler,
        rates: PLAY_RATES.filter((item) => item.show),
        selectedItem: settings.speedRate,
      },
    });

  const toggleQualityModal = () =>
    modalActions.addModal(MODAL_TYPES.MOBILE_QUALITY_LIST, {
      config: {
        hideCloseIcon: true,
      },
      data: {
        sources: sources,
        onQualityChange: qualityChangeHandler,
        selectedItem: settings.quality,
      },
    });

  const RenderPlayIcon = isPlaying ? PauseIcon : PlayIcon;
  const speedRateLabel = PLAY_RATES.find(
    (rate) => rate.id === settings.speedRate
  )?.label;
  const RenderFullScreenIcon = fullScreen ? UnFullscreenIcon : FullscreenIcon;

  const renderVolumeIcon = () => {
    const props = { ...controlsIconProps };
    if (isMuted || volume === 0) return <Mute {...props} />;
    if (volume > 0 && volume <= 0.333) return <VolumeLow {...props} />;
    if (volume > 0.333 && volume <= 0.666) return <VolumeMedium {...props} />;
    return <VolumeHigh {...props} />;
  };

  return (
    <div
      className={classNames(
        "nplay-bottom-controls",
        showGoNextTimer && "smx:hidden"
      )}
    >
      <div className="nplay-left-controls">
        {!isMobile && (
          <Tooltip
            theme={TOOLTIP_THEME.LIGHT}
            showArrow={false}
            message={
              <p className="flex flex-row-reverse">
                <span className="ml-0.5">۱۰</span>
                <span> ثانیه قبل </span>
              </p>
            }
          >
            <button onClick={() => backOrForWardHandler(true)}>
              <BackIcon
                className={classNames(
                  "nply-forward nply-jumptf",
                  fullScreen || isLandscape ? "block" : "hidden"
                )}
                {...controlsIconProps}
              />
            </button>
          </Tooltip>
        )}
        <Tooltip
          theme={TOOLTIP_THEME.LIGHT}
          showArrow={false}
          disabled={isMobile}
          message={isPlaying ? "توقف" : "پخش"}
        >
          <button onClick={togglePlayByClickHandler}>
            <RenderPlayIcon {...controlsIconProps} />
          </button>
        </Tooltip>
        {!isMobile && (
          <Tooltip
            theme={TOOLTIP_THEME.LIGHT}
            showArrow={false}
            message={
              <p className="flex flex-row-reverse">
                <span className="ml-0.5">۱۰</span>
                <span> ثانیه بعد </span>
              </p>
            }
          >
            <button onClick={() => backOrForWardHandler(false)}>
              <FastIcon
                className={classNames(
                  "nply-forward nply-jumptb",
                  fullScreen || isLandscape ? "block" : "hidden"
                )}
                {...controlsIconProps}
              />
            </button>
          </Tooltip>
        )}
        <div
          className="nplay-volume-controls"
          onMouseEnter={toggleShowVolumeProgress}
          onMouseLeave={toggleShowVolumeProgress}
        >
          <Tooltip
            theme={TOOLTIP_THEME.LIGHT}
            showArrow={false}
            disabled={isMobile}
            message={isMuted ? "صدا دار" : "بی‌صدا"}
          >
            <button onClick={toggleMuteHandler}>{renderVolumeIcon()}</button>
          </Tooltip>
          {showVolumeProgress && (
            <VolumeProgress
              volume={volume}
              volumeChangeHandler={volumeChangeHandler}
            />
          )}
        </div>
        <div className="nplay-time">
          <time ref={elapsedRef}>۰۰:۰۰</time>
          <span> / </span>
          <time ref={durationRef}>۰۰:۰۰</time>
        </div>
      </div>
      <div className="right-controls flex items-center">
        {isMobile ? (
          <div className="nplay-rateSpeed" onClick={toggleRateSpeedModal}>
            {/* <SpeedRateIcon {...controlsIconProps} /> */}
            <div className="text-white text-base font-bold leading-8 text-center min-w-[32px]">
              {toFarsiNumber(speedRateLabel)}
            </div>
          </div>
        ) : (
          <SpeedRateList
            onSpeedRateChange={speedRateChangeHandler}
            rates={PLAY_RATES}
            selectedItem={settings.speedRate}
          />
        )}
        {isMobile ? (
          <div className="nplay-qualities_btn" onClick={toggleQualityModal}>
            <QualityIcon {...controlsIconProps} />
          </div>
        ) : (
          <QualityList
            selectedItem={settings.quality}
            sources={sources}
            onQualityChange={qualityChangeHandler}
            isMobile={isMobile}
          />
        )}
        <Tooltip
          theme={TOOLTIP_THEME.LIGHT}
          showArrow={false}
          disabled={isMobile}
          message="تصویر در تصویر"
        >
          <button onClick={pipEnable ? togglePipHandler : undefined}>
            <PipIcon
              className={classNames("pip-button", pipSupported ? "" : "hidden")}
              {...controlsIconProps}
            />
          </button>
        </Tooltip>
        {withSubtitle && (
          <Tooltip
            theme={TOOLTIP_THEME.LIGHT}
            disabled={isMobile}
            message="زیرنویس"
          >
            <button onClick={toggleSubtitle}>
              <SubtitleIcon {...controlsIconProps} />
              <div className="relative">
                {subtitleOn ? (
                  <div className="left-1 right-0 h-[3px] bg-[#1E88E5] absolute -bottom-1.5" />
                ) : null}
              </div>
            </button>
          </Tooltip>
        )}
        <Tooltip
          theme={TOOLTIP_THEME.LIGHT}
          showArrow={false}
          disabled={isMobile}
          message={fullScreen ? "کوچک‌کردن" : "تمام ‌صفحه"}
        >
          <button
            onClick={fullScreenEnable ? toggleFullscreenHandler : undefined}
          >
            <RenderFullScreenIcon
              className="nplay-fullscreen-button"
              {...controlsIconProps}
            />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default VideoControlsBottomActions;
